:host {
  /** @prop --textarea-base-max-width: Set the base max width of the textarea */
  --textarea-base-max-width: 328px;
  /** @prop --textarea-base-width: Set the base width of the textarea */
  --textarea-base-width: 100%;
  /** @prop --textarea-base-height: Set the base height of the textarea to 3 times the height of a standard component  */
  --textarea-base-height: calc(3 * var(--p-size-dynamic-600));
  /** @prop --textarea-outline: Set the color of the outline */
  --textarea-outline: var(--s-color-outline-default);
  /** @prop --textarea-border-base-width: Set the base width of the border */
  --textarea-border-base-width: var(--s-size-border-width-default);
  /** @prop --textarea-border-radius: Set the border radius */
  --textarea-border-radius: var(--s-size-radius-medium);
  /** @prop --textarea-border-color: Set the border color */
  --textarea-border-color: var(--s-color-border-default);
  /** @prop --textarea-border-color-hover: Set the border color on hover */
  --textarea-border-color-hover: var(--s-color-outline-default);
  /** @prop --textarea-border-color-focus: Set the border color on focus */
  --textarea-border-color-focus: var(--s-color-outline-default);
  /** @prop --textarea-border-color-error: Set the border color for error states */
  --textarea-border-color-error: var(--s-color-border-error);
  /** @prop --textarea-color-read-only: Set the border color for a readonly textarea */
  --textarea-border-color-readonly: var(--s-color-border-divider);
  /** @prop --textarea-border-color-disabled: Set the border color for a disabled textarea */
  --textarea-border-color-disabled: var(--b-color-neutral-lighter);
  /** @prop --textarea-line-height: Set the line height of the textarea */
  --textarea-line-height: var(--p-size-dynamic-300);

  display: block;
  max-width: var(--textarea-base-max-width);
}

$BASE_CLASS: 'anchor-c-textarea';

@mixin msg-elm {
  display: block;
  margin-top: calc(var(--p-size-static-100) * -1);
  color: var(--s-color-text-default);
  text-align: left;
  font-weight: var(--s-font-weight-label-default);
  line-height: var(--p-size-dynamic-300);
}

@mixin error-msg-elm {
  display: flex;
  gap: var(--p-size-static-200);
  justify-content: space-between;
  margin: 0 0 var(--p-size-static-100);
  background-color: var(--b-color-error-light);
  padding: var(--p-size-static-100) var(--p-size-static-200);
  border-radius: var(--s-size-radius-medium);
  line-height: var(--p-size-dynamic-300);
}

.#{$BASE_CLASS}__container {
  display: flex;
  flex-direction: column;
  gap: var(--p-size-static-100);

  &--error {
    border-left: var(--p-size-static-050) var(--s-color-border-error) solid;
    padding-left: var(--p-size-static-200);
  }

  &--no-resize {
    .#{$BASE_CLASS} {
      resize: none;
    }
  }

  &--disabled {
    .#{$BASE_CLASS} {
      cursor: not-allowed;
    }
    .anchor-c-textarea__label,
    .anchor-c-textarea__help-msg {
      color: var(--s-color-text-disabled);
    }
  }

  &--readonly {
    .#{$BASE_CLASS} {
      cursor: default;
    }
  }

  &--set-width {
    --textarea-width: unset;
  }
}

.#{$BASE_CLASS} {
  --textarea-width: var(--textarea-base-width); // calc(var(--textarea-base-width) - var(--textarea-border-base-width) * 2);

  // Expanded variables
  --textarea-padding-y: var(--p-size-static-150);
  --textarea-padding-x: var(--p-size-static-200);

  --textarea-border-width: var(--textarea-border-base-width);
  --border-radius: var(--textarea-border-radius);
  --border-color: var(--textarea-border-color);
  --border-color-hover: var(--textarea-border-color-hover);
  --border-color-focus: var(--textarea-border-color-focus);
  --border-color-warning: var(--textarea-border-color-warning);
  --border-color-error: var(--textarea-border-color-error);
  --border-color-readonly: var(--textarea-border-color-readonly);
  --border-color-disabled: var(--textarea-border-color-disabled);

  width: var(--textarea-width);
  min-height: var(--textarea-base-height);
  box-sizing: border-box;
  font-family: inherit;
  line-height: var(--textarea-line-height);
  font-size: var(--s-size-text-default);
  text-align: left;
  color: var(--s-color-text-default);
  cursor: text;
  resize: vertical;
  padding: calc(var(--textarea-padding-y) - var(--textarea-border-width)) calc(var(--textarea-padding-x) - var(--textarea-border-width));
  border-radius: var(--textarea-border-radius);
  border: var(--textarea-border-width) solid var(--textarea-border-color);

  &:hover {
    --border-color: var(--border-color-hover);
    outline: var(--s-color-outline-aura-hover) solid var(--s-size-outline-aura-width);
  }
  &:focus-visible,
  &:focus {
    --textarea-border-color: var(--textarea-border-color-focus);
    outline: var(--textarea-outline) solid var(--s-size-outline-focus-ring-width);
    outline-offset: var(--s-size-outline-focus-ring-offset);
  }
  &--warning {
    --textarea-border-color: var(--textarea-border-color-warning);
  }
  &--error {
    --textarea-border-color: var(--textarea-border-color-error);
  }
  &::placeholder {
    color: var(--b-color-neutral-dark);
  }
  &[readonly=''] {
    --textarea-border-width: 0;
    background-color: transparent;
    color: var(--s-color-text-contrast);
    font-weight: 600;
    outline: none;
    resize: none;
  }
  &[disabled=''] {
    --textarea-border-color: var(--textarea-border-color-disabled);
    background-color: var(--b-color-neutral-lighter);
    color: var(--s-color-text-disabled);
    outline: none;
    resize: none;
    &:hover {
      cursor: not-allowed;
    }
  }

  &__headarea {
    display: flex;
    justify-content: space-between;
    align-items: center;
    line-height: var(--textarea-line-height);

    anchor-label {
      order: 1;
      flex: 1 1 auto;
      anchor-icon {
        margin-left: var(--p-size-static-100);
      }
    }

    span {
      order: 2;
      flex: 2 auto;
      text-align: right;

      & + anchor-label {
        flex-grow: 0;
      }
    }
  }

  &__help-msg {
    @include msg-elm();
  }
  &__warning-msg {
    @include msg-elm();
    font-style: italic;
  }
  &__error-msg {
    @include error-msg-elm();
  }

  &__label {
    order: 1;
    display: flex;
    justify-content: space-between;
    align-items: center;
    &-icon {
      display: inline-flex;
    }
  }

  &__maxlength {
    order: 4;
  }

  &__messages {
    &--inside {
      order: 2;
    }
    &--below {
      order: 5;
      .#{$BASE_CLASS}__help-msg {
        margin-top: 0;
      }
      .#{$BASE_CLASS}__error-msg {
        margin: 0;
      }
    }
  }

  &--set-width {
    --input-width: unset;
  }

  &__wrapper {
    position: relative;
    order: 3;
  }
}

anchor-textarea {
  .anchor-c-textarea__messages {
    [slot|='error'] {
      @include error-msg-elm();
    }

    &--inside {
      [slot|='error'] {
        margin: 0.25rem 0;
      }
    }
  }
}
